import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import ResumeCard from '../../components/resume/ResumeCard';
import { PlusIcon, ChevronDownIcon, CheckIcon } from '@heroicons/react/24/solid';
import CreateResumeModal from '../../components/resume/CreateResumeModal';

const ResumeDashboardPage = () => {
    const [resumes, setResumes] = useState([]); // State to hold resumes from the database
    const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
    const [currentSort, setCurrentSort] = useState('date');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate(); // Hook for navigation

    const sortOptions = {
        date: 'Edited',
        alphabetical: 'Name',
    };

    // --- Fetch resumes from the server when the component loads ---
    useEffect(() => {
        const fetchResumes = async () => {
            try {
                // Add a cache-busting query parameter to force a fresh fetch
                const response = await fetch(`https://renaisons.com/api/get_resumes.php?t=${new Date().getTime()}`,
                    {
                        credentials: 'include',
                        cache: 'no-store' // Force browser not to cache the API response
                    });
                const data = await response.json();

                if (data.status === 'success') {
                    const sorted = data.resumes.sort((a, b) => new Date(b.last_edited) - new Date(a.last_edited));
                    setResumes(sorted);
                } else {
                    console.error("Failed to fetch resumes:", data.message);
                }
            } catch (error) {
                console.error("Error fetching resumes:", error);
            }
        };

        fetchResumes();
    }, []); // Runs on mount

    const handleSort = (sortType) => {
        setCurrentSort(sortType);
        const dataToSort = [...resumes];
        if (sortType === 'date') {
            dataToSort.sort((a, b) => new Date(b.last_edited) - new Date(a.last_edited));
        } else if (sortType === 'alphabetical') {
            dataToSort.sort((a, b) => a.resume_name.localeCompare(b.resume_name));
        }
        setResumes(dataToSort);
        setIsSortMenuOpen(false);
    };

    // --- Function to handle clicking on a resume card ---
    const handleResumeClick = (resume) => {
        // Navigate straight to the FinalResumePage (optimization/preview)
        navigate(`/resume/${resume.resume_id}/final`, { state: { resumeName: resume.resume_name } });
    };

    return (
        <div className="px-6 py-10 text-white">
            <CreateResumeModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
            <div className="mx-auto max-w-7xl">
                {/* Header Section */}
                <header className="mb-8 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Resumes</h1>
                        <p className="mt-1 text-sm text-neutral-400">Manage your resumes and cover letters.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Sort */}
                        <div className="relative">
                            <button
                                onClick={() => setIsSortMenuOpen(!isSortMenuOpen)}
                                className="flex items-center rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-neutral-300 transition-colors hover:bg-white/10 hover:text-white"
                            >
                                <span>{sortOptions[currentSort]}</span>
                                <ChevronDownIcon className="ml-2 h-4 w-4" />
                            </button>
                            {isSortMenuOpen && (
                                <div className="absolute right-0 z-10 mt-2 w-44 rounded-lg border border-white/10 bg-[#0b0e14] shadow-2xl">
                                    <ul className="py-1">
                                        <li
                                            onClick={() => handleSort('date')}
                                            className="flex cursor-pointer items-center justify-between px-4 py-2 text-sm text-neutral-200 hover:bg-white/5"
                                        >
                                            Edited
                                            {currentSort === 'date' && <CheckIcon className="h-4 w-4 text-cyan-400" />}
                                        </li>
                                        <li
                                            onClick={() => handleSort('alphabetical')}
                                            className="flex cursor-pointer items-center justify-between px-4 py-2 text-sm text-neutral-200 hover:bg-white/5"
                                        >
                                            Name
                                            {currentSort === 'alphabetical' && <CheckIcon className="h-4 w-4 text-cyan-400" />}
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => navigate('/upgrade')}
                            className="rounded-lg bg-cyan-500 px-5 py-2 font-semibold text-black shadow-lg shadow-cyan-500/20 transition-colors hover:bg-cyan-400"
                        >
                            Upgrade
                        </button>
                    </div>
                </header>

                <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(180px,1fr))]">
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="flex h-full min-h-[240px] cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-white/15 bg-white/[0.02] transition-colors duration-300 hover:border-cyan-500/60 hover:bg-cyan-500/5">
                        <div className="text-center">
                            <PlusIcon className="mx-auto h-8 w-8 text-neutral-500" />
                            <p className="mt-2 text-sm font-semibold text-neutral-400">Create new resume</p>
                        </div>
                    </button>

                    {/* --- Map over the fetched resumes and render a card for each --- */}
                    {resumes.map((resume) => (
                        <div key={resume.resume_id} onClick={() => handleResumeClick(resume)}>
                            <ResumeCard resume={resume} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ResumeDashboardPage;